import { useEffect, useState, type FormEvent } from "react";
import { MinusIcon, PlusIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { ProgressDots } from "@/components/ui/progress-dots";
import { focusRing } from "@/lib/format";

interface StepperProps {
  value: number;
  pending?: boolean;
  minusDisabled?: boolean;
  plusDisabled?: boolean;
  onDecrement: () => void;
  onIncrement: () => void;
  /** Called when the user types a value and commits via Enter or blur. If
   *  omitted, the value renders as static text and isn't editable. */
  onCommit?: (next: number) => void;
  /** Smallest allowed value when committing typed input. Defaults to 0. */
  minValue?: number;
  size?: "sm" | "md";
  className?: string;
  decrementAriaLabel?: string;
  incrementAriaLabel?: string;
}

// Horizontal -/n/+ stepper used in cart line rows. Pending state replaces the
// numeric value with a ProgressDots glyph, disables both buttons, and dims the
// whole container so the "thinking" state reads on the buttons as well as the
// value. The vertical-roll animation (§5.3) is parked for v1.1 in favor of
// this simpler approach.
export function Stepper({
  value,
  pending = false,
  minusDisabled = false,
  plusDisabled = false,
  onDecrement,
  onIncrement,
  onCommit,
  minValue = 0,
  size = "md",
  className,
  decrementAriaLabel = "Reducir cantidad",
  incrementAriaLabel = "Aumentar cantidad",
}: StepperProps) {
  const buttonSize = size === "sm" ? "size-9" : "size-8";
  const valueBox = size === "sm" ? "h-9 w-9 text-[13px]" : "h-8 w-9 text-[13px]";

  // Local draft so the user can type freely without re-rendering on every
  // keystroke. We commit on blur/Enter; Escape reverts. Whenever the upstream
  // value changes (e.g. mutation completes), reseed the draft.
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);

  function commit() {
    if (!onCommit) return;
    const trimmed = draft.trim();
    if (trimmed === "" || trimmed === String(value)) {
      setDraft(String(value));
      return;
    }
    const parsed = Math.floor(Number(trimmed));
    if (!Number.isFinite(parsed)) { setDraft(String(value)); return; }
    const next = Math.max(minValue, parsed);
    if (next === value) { setDraft(String(value)); return; }
    onCommit(next);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    (e.currentTarget as HTMLFormElement).querySelector("input")?.blur();
  }

  return (
    <div
      className={cn(
        "inline-flex shrink-0 items-center rounded-md border border-input bg-background transition-opacity duration-150",
        pending && "opacity-70",
        className,
      )}
    >
      <button
        type="button"
        onClick={onDecrement}
        disabled={pending || minusDisabled}
        aria-disabled={pending || minusDisabled}
        aria-label={decrementAriaLabel}
        className={cn(
          buttonSize,
          "grid place-items-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-muted-foreground",
          focusRing,
        )}
      >
        <MinusIcon className="size-3.5" />
      </button>
      {pending ? (
        <span className={cn(valueBox, "grid place-items-center font-semibold tabular-nums")}>
          <ProgressDots aria-label="Actualizando" />
        </span>
      ) : onCommit ? (
        <form onSubmit={onSubmit} className="contents">
          <input
            type="number"
            inputMode="numeric"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setDraft(String(value));
                (e.target as HTMLInputElement).blur();
              }
            }}
            min={minValue}
            aria-label="Cantidad"
            className={cn(
              valueBox,
              "border-0 bg-transparent text-center font-semibold tabular-nums outline-none focus:bg-muted/50",
              "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
              focusRing,
            )}
          />
        </form>
      ) : (
        <span
          className={cn(valueBox, "grid place-items-center font-semibold tabular-nums")}
          aria-live="polite"
        >
          {value}
        </span>
      )}
      <button
        type="button"
        onClick={onIncrement}
        disabled={pending || plusDisabled}
        aria-disabled={pending || plusDisabled}
        aria-label={incrementAriaLabel}
        className={cn(
          buttonSize,
          "grid place-items-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-muted-foreground",
          focusRing,
        )}
      >
        <PlusIcon className="size-3.5" />
      </button>
    </div>
  );
}
